import { Sparkles } from 'lucide-react';
/**
 * Natural-language segment builder. Talks to /api/copilot/segment-from-nl
 * and emits the parsed SegmentFilter via onChange. Falls back gracefully
 * (silent error message) when the AI is not configured.
 */
import { useState } from 'react';
import type { SegmentFilter } from './types';

interface Props {
  onApply: (f: SegmentFilter) => void;
}

export function NlSegmentBuilder({ onApply }: Props) {
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run() {
    if (text.trim().length === 0) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/copilot/segment-from-nl', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ query: text }),
      });
      if (res.status === 503) {
        setError('AI Copilot is not configured for this workspace. Use the pill builder below.');
        return;
      }
      if (!res.ok) {
        const b = (await res.json().catch(() => ({}))) as { issues?: string[]; message?: string };
        setError(b.issues?.join(', ') ?? b.message ?? `Error (${res.status})`);
        return;
      }
      const body = (await res.json()) as { filter: SegmentFilter };
      onApply(body.filter);
      setText('');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="rounded-md border border-border bg-secondary/30 p-3 space-y-2">
      <label
        htmlFor="nl-segment"
        className="text-xs font-medium flex items-center gap-1 text-muted-foreground"
      >
        <Sparkles className="size-3" /> Describe your audience in plain English
      </label>
      <div className="flex gap-2">
        <textarea
          id="nl-segment"
          rows={2}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="e.g. dog owners who haven't visited in 3 months and have spent over £200"
          className="flex-1 border rounded px-2 py-1 text-sm"
        />
        <button
          type="button"
          onClick={run}
          disabled={loading || text.trim().length === 0}
          className="rounded-md bg-primary text-primary-foreground px-3 py-2 text-sm font-medium disabled:opacity-50"
        >
          {loading ? '…' : 'Build segment'}
        </button>
      </div>
      {error && <div className="text-xs text-red-700">{error}</div>}
    </div>
  );
}
